import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Inbox, Clock, Users, ChevronRight, Trash2, Plus, FileText, MoreVertical, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUSES = ["novo", "em andamento", "concluído"];
const APPS = [
  { id: "biblioteca", name: "Gestão de Biblioteca" },
  { id: "pos-lojas", name: "POS Lojas" },
  { id: "pos-restaurantes", name: "POS Restaurantes" },
  { id: "custom", name: "Projeto Sob Medida" },
];
const API_SLUGS = { biblioteca: "biblioteca", "pos-lojas": "pos", "pos-restaurantes": "restaurant", custom: "custom" };
const emptyUser = { name: "", email: "", password: "", role: "user", licenses: [] };

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ total: 0, novos: 0, usuarios: 0 });
  const [users, setUsers] = useState([]);
  const [active, setActive] = useState(null);
  const [nu, setNu] = useState(emptyUser);
  const [reportFor, setReportFor] = useState(null);
  const [report, setReport] = useState({ title: "", period: "", revenue: "", notes: "" });
  const [menuOpen, setMenuOpen] = useState(false);
  const [licenses, setLicenses] = useState([]);
  const [apps, setApps] = useState([]);
  const [licenseApp, setLicenseApp] = useState("biblioteca");
  const [licenseEmail, setLicenseEmail] = useState("");
  const [editingApp, setEditingApp] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserLicenses, setSelectedUserLicenses] = useState([]);
  const [selectedUserDevices, setSelectedUserDevices] = useState([]);
  const [selectedLicenseApp, setSelectedLicenseApp] = useState("biblioteca");

  const load = async () => {
    const [o, s, u, l, a] = await Promise.allSettled([api.get("/orders"), api.get("/stats"), api.get("/users"), api.get("/licenses"), api.get("/apps")]);
    if (o.status === "fulfilled") setOrders(o.value.data);
    if (s.status === "fulfilled") setStats(s.value.data);
    if (u.status === "fulfilled") setUsers(u.value.data);
    if (l.status === "fulfilled") setLicenses(l.value.data);
    if (a.status === "fulfilled") setApps(a.value.data);
    if ([o, s, u, l, a].some((r) => r.status === "rejected" && r.reason?.response?.status !== 401))
      toast.error("Falha ao carregar parte do painel. Tente novamente.");
  };
  useEffect(() => { load(); }, []);

  const doLogout = () => { logout(); navigate("/login", { replace: true }); };

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/orders/${id}`, { status });
      toast.success("Status atualizado."); load();
      setActive((a) => (a ? { ...a, status } : a));
    } catch (err) { toast.error(err.response?.data?.detail || "Erro."); }
  };

  const toggleLic = (id) =>
    setNu((p) => ({ ...p, licenses: p.licenses.includes(id) ? p.licenses.filter((x) => x !== id) : [...p.licenses, id] }));

  const createUser = async (e) => {
    e.preventDefault();
    if (!nu.email || nu.password.length < 6) { toast.error("Informe e-mail e senha (mín. 6)."); return; }
    try {
      await api.post("/users", nu);
      toast.success("Usuário criado. Ele deverá trocar a senha no 1º login.");
      setNu(emptyUser); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao criar usuário."); }
  };

  const delUser = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este usuário? Esta ação não pode ser desfeita.")) return;
    try { await api.delete(`/users/${id}`); toast.success("Usuário removido."); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erro."); }
  };

  const submitReport = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/users/${reportFor.id}/reports`, {
        title: report.title, period: report.period,
        revenue: parseFloat(report.revenue) || 0, notes: report.notes,
      });
      toast.success("Relatório adicionado.");
      setReportFor(null); setReport({ title: "", period: "", revenue: "", notes: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Erro."); }
  };

  const createLicense = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post("/licenses", { app_id: licenseApp, user_email: licenseEmail });
      setLicenses((current) => [response.data, ...current]);
      setLicenseEmail("");
      toast.success("Licença criada e associada.");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao criar licença."); }
  };

  const saveApp = async (e) => {
    e.preventDefault();
    try {
      const response = await api.patch(`/apps/${editingApp.id}`, editingApp);
      setApps((current) => current.map((app) => app.id === response.data.id ? response.data : app));
      setEditingApp(null);
      toast.success("App atualizado.");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao atualizar app."); }
  };

  const deleteApp = async (id) => {
    if (!window.confirm("Excluir este app do catálogo?")) return;
    try {
      await api.delete(`/apps/${id}`);
      setApps((current) => current.filter((app) => app.id !== id));
      toast.success("App excluído.");
    } catch (err) { toast.error(err.response?.data?.detail || "O app padrão não pode ser excluído."); }
  };

  const openUserLicenses = async (account) => {
    try {
      const [licenseResponse, deviceResponse] = await Promise.all([api.get(`/users/${account.id}/licenses`), api.get(`/users/${account.id}/devices`)]);
      setSelectedUser(account);
      setSelectedUserLicenses(licenseResponse.data);
      setSelectedUserDevices(deviceResponse.data);
      setSelectedLicenseApp((apps[0] || APPS[0]).id);
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao carregar licenças."); }
  };

  const revokeDevice = async (deviceId) => {
    try {
      await api.delete(`/users/${selectedUser.id}/devices/${deviceId}`);
      setSelectedUserDevices((current) => current.filter((device) => device.id !== deviceId));
      toast.success("Dispositivo revogado.");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao revogar dispositivo."); }
  };

  const addLicenseToSelectedUser = async () => {
    try {
      const response = await api.post(`/users/${selectedUser.id}/licenses`, { app_id: selectedLicenseApp });
      setSelectedUserLicenses((current) => [...current, response.data]);
      setUsers((current) => current.map((account) => account.id === selectedUser.id ? { ...account, licenses: [...(account.licenses || []), selectedLicenseApp] } : account));
      toast.success("App adicionado à conta.");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao adicionar app."); }
  };

  const metrics = [
    { label: "Total de Pedidos", value: stats.total, icon: Inbox },
    { label: "Leads Novos", value: stats.novos, icon: Clock },
    { label: "Usuários", value: stats.usuarios, icon: Users },
  ];
  const field = "w-full bg-ink border border-white/15 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-cobalt transition-colors";

  return (
    <div className="min-h-screen grain">
      <header className="border-b border-white/10 bg-ink/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-full.png" alt="JeaxLabs" className="h-7 w-auto" />
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-white/40 hidden sm:inline">Painel Admin</span>
          </div>
          <button onClick={doLogout} data-testid="admin-logout"
                  className="hidden sm:flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] px-4 py-2 border border-white/15 hover:bg-white hover:text-ink transition-colors">
            <LogOut className="w-4 h-4" /> Sair
          </button>
          <button onClick={() => setMenuOpen((open) => !open)} className="sm:hidden p-2" aria-label="Abrir menu" data-testid="admin-menu">
            {menuOpen ? <X className="w-5 h-5" /> : <MoreVertical className="w-5 h-5" />}
          </button>
          {menuOpen && <button onClick={doLogout} data-testid="admin-logout-mobile"
            className="sm:hidden absolute right-5 top-14 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] px-4 py-3 bg-ink border border-white/15">
            <LogOut className="w-4 h-4" /> Sair
          </button>}
        </div>

      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-10 py-10">
        <h1 className="font-head font-black text-3xl md:text-5xl tracking-tighter mb-8">Painel de controle</h1>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-14">
          {metrics.map((m) => (
            <div key={m.label} className="border border-white/10 bg-surface/40 p-6" data-testid={`stat-${m.label}`}>
              <div className="flex items-center justify-between mb-6">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-white/50">{m.label}</span>
                <m.icon className="w-5 h-5 text-cobalt" />
              </div>
              <span className="font-head font-black text-5xl">{m.value}</span>
            </div>
          ))}
        </div>

        {/* Orders */}
        <h2 className="font-head font-black text-2xl md:text-3xl tracking-tight mb-6">Pedidos recebidos</h2>
        {orders.length === 0 ? (
          <div className="border border-white/10 p-10 text-center text-white/40 font-mono text-sm mb-14" data-testid="admin-empty">
            Nenhum pedido ainda.
          </div>
        ) : (
          <div className="border border-white/10 divide-y divide-white/10 mb-14" data-testid="orders-table">
            {orders.map((o) => (
              <button key={o.id} onClick={() => setActive(o)} data-testid={`order-row-${o.id}`}
                      className="w-full text-left grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-5 py-4 hover:bg-surface/40 transition-colors items-center">
                <span className="md:col-span-3 font-medium">{o.name}</span>
                <span className="md:col-span-3 text-white/50 text-sm truncate">{o.contact}</span>
                <span className="md:col-span-3 text-cobalt text-sm">{o.recommended_app}</span>
                <span className="md:col-span-2">
                  <span className={`font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 border ${
                    o.status === "novo" ? "border-acid/50 text-acid" : o.status === "concluído" ? "border-white/20 text-white/40" : "border-cobalt/50 text-cobalt"}`}>{o.status}</span>
                </span>
                <span className="md:col-span-1 hidden md:flex justify-end"><ChevronRight className="w-4 h-4 text-white/30" /></span>
              </button>
            ))}
          </div>
        )}

        {/* Users */}
        <h2 className="font-head font-black text-2xl md:text-3xl tracking-tight mb-6">Usuários & Licenças</h2>
        <div className="grid lg:grid-cols-2 gap-6">
          <form onSubmit={createUser} className="border border-white/10 bg-surface/40 p-6 md:p-8 space-y-3" data-testid="create-user-form">
            <h3 className="font-head font-bold text-lg mb-2">Criar novo usuário</h3>
            <input className={field} placeholder="Nome" data-testid="user-name" value={nu.name} onChange={(e) => setNu({ ...nu, name: e.target.value })} />
            <input className={field} type="email" placeholder="E-mail" data-testid="user-email" value={nu.email} onChange={(e) => setNu({ ...nu, email: e.target.value })} />
            <input className={field} type="password" placeholder="Senha provisória (mín. 6)" data-testid="user-password" value={nu.password} onChange={(e) => setNu({ ...nu, password: e.target.value })} />
            <select className={field} data-testid="user-role" value={nu.role} onChange={(e) => setNu({ ...nu, role: e.target.value })}>
              <option value="user">Usuário</option>
              <option value="admin">Administrador</option>
            </select>
            <div>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-white/40 block mb-2">Licenças</span>
              <div className="grid grid-cols-2 gap-2">
                {(apps.length ? apps : APPS).map((a) => (
                  <button type="button" key={a.id} onClick={() => toggleLic(a.id)} data-testid={`lic-${a.id}`}
                          className={`font-mono text-[11px] uppercase tracking-[0.1em] px-3 py-2 border text-left transition-colors ${
                            nu.licenses.includes(a.id) ? "bg-cobalt border-cobalt text-white" : "border-white/20 text-white/60 hover:border-white"}`}>
                    {a.name}
                  </button>
                ))}
              </div>
            </div>
            <button type="submit" data-testid="create-user-submit"
                    className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 bg-cobalt text-white hover:bg-white hover:text-ink transition-colors">
              <Plus className="w-4 h-4" /> Criar usuário
            </button>
            <p className="text-white/40 text-xs">O usuário será obrigado a trocar a senha no primeiro login.</p>
          </form>

          <div className="border border-white/10 divide-y divide-white/10" data-testid="users-list">
            {users.map((u) => (
              <button key={u.id} onClick={() => openUserLicenses(u)} className="w-full px-5 py-4 flex items-center justify-between gap-3 text-left hover:bg-surface/50 transition-colors" data-testid={`user-${u.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{u.name || u.email}</span>
                    <span className={`font-mono text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 border ${u.role === "admin" ? "border-acid/50 text-acid" : "border-white/20 text-white/50"}`}>{u.role}</span>
                  </div>
                  <div className="text-white/40 text-xs truncate">{u.email} · {(u.licenses || []).length} licença(s)</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span onClick={(e) => { e.stopPropagation(); setReportFor(u); }} data-testid={`add-report-${u.id}`} title="Adicionar relatório"
                      className="p-2 border border-white/15 hover:border-cobalt text-white/60 hover:text-white transition-colors"><FileText className="w-4 h-4" /></span>
                  {u.id !== user?.id && (
                    <span onClick={(e) => { e.stopPropagation(); delUser(u.id); }} data-testid={`del-user-${u.id}`} title="Excluir"
                          className="p-2 border border-white/15 hover:border-red-500 text-white/60 hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        <section className="mt-14" data-testid="admin-licenses">
          <h2 className="font-head font-black text-2xl md:text-3xl tracking-tight mb-6">Licenças de uso</h2>
          <form onSubmit={createLicense} className="border border-white/10 bg-surface/40 p-6 grid md:grid-cols-3 gap-3 mb-4">
            <select className={field} value={licenseApp} onChange={(e) => setLicenseApp(e.target.value)}>
              {(apps.length ? apps : APPS).map((app) => <option key={app.id} value={app.id}>{app.name}</option>)}
            </select>
            <input className={field} type="email" placeholder="E-mail da conta (opcional)" value={licenseEmail} onChange={(e) => setLicenseEmail(e.target.value)} />
            <button type="submit" className="inline-flex items-center justify-center gap-2 font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 bg-cobalt text-white"><Plus className="w-4 h-4" /> Gerar licença</button>
          </form>
          <div className="border border-white/10 divide-y divide-white/10">
            {licenses.map((license) => <div key={license.code} className="px-5 py-3 flex flex-wrap justify-between gap-2 text-sm"><span>{license.app_name}</span><span className="text-white/40">{license.user_email || "Sem conta"}</span></div>)}
          </div>
        </section>

        <section className="mt-14" data-testid="admin-apps">
          <h2 className="font-head font-black text-2xl md:text-3xl tracking-tight mb-6">Catálogo de apps</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {apps.map((app) => <div key={app.id} className="border border-white/10 bg-surface/40 p-5 flex items-center justify-between gap-4"><div className="min-w-0"><h3 className="font-head font-bold truncate">{app.name}</h3><p className="font-mono text-sm text-cobalt">{app.price}</p><p className="font-mono text-[11px] text-white/40 truncate mt-2">API: {window.location.origin}/api/{API_SLUGS[app.id] || app.id}</p></div><div className="flex gap-2 shrink-0"><button onClick={() => setEditingApp({ ...app })} title="Editar app" className="p-2 border border-white/15"><FileText className="w-4 h-4" /></button><button onClick={() => deleteApp(app.id)} title="Excluir app" className="p-2 border border-white/15 text-red-400"><Trash2 className="w-4 h-4" /></button></div></div>)}
          </div>
        </section>
      </main>

      {/* Order drawer */}
      {active && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="order-detail">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setActive(null)} />
          <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} transition={{ ease: [0.76, 0, 0.24, 1], duration: 0.5 }}
                      className="relative w-full max-w-md bg-ink border-l border-white/10 h-full overflow-y-auto p-8">
            <button onClick={() => setActive(null)} className="font-mono text-xs uppercase tracking-[0.2em] text-white/50 hover:text-white mb-8" data-testid="order-detail-close">✕ Fechar</button>
            <h2 className="font-head font-black text-3xl tracking-tight mb-1">{active.name}</h2>
            <p className="text-white/50 mb-8">{active.contact}</p>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-white/40">Pedido</span>
            <p className="mt-2 mb-6 text-white/80 leading-relaxed">{active.description}</p>
            <div className="border border-cobalt/40 bg-cobalt/5 p-5 mb-6">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-acid">Recomendação</span>
              <h3 className="font-head font-bold text-xl mt-2">{active.recommended_app}</h3>
              <p className="font-mono text-cobalt text-sm mb-3">{active.recommended_plan}</p>
              <p className="text-white/70 text-sm leading-relaxed">{active.reasoning}</p>
            </div>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-white/40 block mb-3">Alterar status</span>
            <div className="flex flex-wrap gap-2">
              {STATUSES.map((s) => (
                <button key={s} onClick={() => changeStatus(active.id, s)} data-testid={`status-${s}`}
                        className={`font-mono text-xs uppercase tracking-[0.15em] px-3 py-2 border transition-colors ${active.status === s ? "bg-cobalt border-cobalt text-white" : "border-white/20 hover:border-white"}`}>{s}</button>
              ))}
            </div>
          </motion.div>
        </div>
      )}

      {/* Report modal */}
      {reportFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-5" data-testid="report-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setReportFor(null)} />
          <motion.form onSubmit={submitReport} initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                       className="relative w-full max-w-md bg-ink border border-white/10 p-8 space-y-3">
            <h3 className="font-head font-bold text-xl mb-1">Relatório para {reportFor.name || reportFor.email}</h3>
            <input className={field} placeholder="Título (ex: Vendas de Junho)" data-testid="report-title" value={report.title} onChange={(e) => setReport({ ...report, title: e.target.value })} />
            <input className={field} placeholder="Período (ex: 06/2026)" value={report.period} onChange={(e) => setReport({ ...report, period: e.target.value })} />
            <input className={field} type="number" step="0.01" placeholder="Faturamento (R$)" value={report.revenue} onChange={(e) => setReport({ ...report, revenue: e.target.value })} />
            <textarea className={field} rows={3} placeholder="Observações" value={report.notes} onChange={(e) => setReport({ ...report, notes: e.target.value })} />
            <div className="flex gap-3">
              <button type="submit" data-testid="report-submit" className="font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 bg-cobalt text-white hover:bg-white hover:text-ink transition-colors">Salvar</button>
              <button type="button" onClick={() => setReportFor(null)} className="font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 border border-white/20 hover:border-white transition-colors">Cancelar</button>
            </div>
          </motion.form>
        </div>
      )}
      {editingApp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-5" data-testid="app-edit-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => setEditingApp(null)} />
          <motion.form onSubmit={saveApp} className="relative w-full max-w-md bg-ink border border-white/10 p-8 space-y-3">
            <h3 className="font-head font-bold text-xl">Editar app</h3>
            <input className={field} value={editingApp.name} onChange={(e) => setEditingApp({ ...editingApp, name: e.target.value })} placeholder="Nome" />
            <input className={field} value={editingApp.price} onChange={(e) => setEditingApp({ ...editingApp, price: e.target.value })} placeholder="Preço" />
            <input className={field} value={editingApp.image || ""} onChange={(e) => setEditingApp({ ...editingApp, image: e.target.value })} placeholder="URL da foto" />
            <div className="flex gap-3"><button type="submit" className="font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 bg-cobalt">Salvar</button><button type="button" onClick={() => setEditingApp(null)} className="font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 border border-white/20">Cancelar</button></div>
          </motion.form>
        </div>
      )}
      {selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-5" data-testid="user-licenses-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => setSelectedUser(null)} />
          <div className="relative w-full max-w-lg bg-ink border border-white/10 p-8">
            <div className="flex items-start justify-between gap-4 mb-6"><div><h3 className="font-head font-bold text-xl">Licenças de {selectedUser.name || selectedUser.email}</h3><p className="text-white/40 text-sm">{selectedUser.email}</p></div><button onClick={() => setSelectedUser(null)} className="font-mono text-xs">Fechar</button></div>
            <div className="space-y-2 mb-6">{selectedUserLicenses.map((license) => <div key={license.code} className="border border-white/10 px-4 py-3 flex flex-wrap justify-between gap-2"><span>{license.app_name}</span><span className="text-white/40">Ativa</span></div>)}</div>
            <h4 className="font-mono text-xs uppercase tracking-[0.15em] text-white/40 mb-2">Dispositivos autorizados</h4>
            <div className="space-y-2 mb-6">{selectedUserDevices.map((device) => <div key={device.id} className="border border-white/10 px-4 py-3 flex items-center justify-between gap-3"><span className="text-sm">{device.app_id} <span className="text-white/40">até {new Date(device.expires_at).toLocaleDateString()}</span></span><button onClick={() => revokeDevice(device.id)} className="font-mono text-[10px] uppercase text-red-400">Revogar</button></div>)}</div>
            <div className="flex gap-2"><select className={field} value={selectedLicenseApp} onChange={(e) => setSelectedLicenseApp(e.target.value)}>{(apps.length ? apps : APPS).map((app) => <option key={app.id} value={app.id}>{app.name}</option>)}</select><button onClick={addLicenseToSelectedUser} className="shrink-0 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.1em] px-4 py-3 bg-cobalt"><Plus className="w-4 h-4" /> App</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
